/* global axios */
import { useAccount } from 'dashboard/composables/useAccount';

// Тот же обход JwtAuthGuard NestJS, что и в OvvaChatMenuBar.vue: у сессии
// Chatwoot нет JWT нашего бэкенда, поэтому берём короткоживущий токен через
// Rails (Api::V1::Accounts::OvvaChatTokensController → Ovva::EmbedTokenService)
// перед КАЖДЫМ вызовом — токен живёт минуты, кешировать его нельзя.
//
// x-chatwoot-token (PAT) — вторая, более строгая ступень доступа для
// чувствительных ручек (настройки AI, ключи каналов, /system/integrations,
// /setup/register-webhooks — см. SuperAdminPatGuard на бэкенде). Личный Access
// Token оператора (Профиль → Настройки профиля → Access Token), вводится один
// раз и живёт в localStorage браузера — тот же приём, что в старой embed.ejs
// (ключ 'ovva_cw_pat'). Bearer-токен шлётся всегда, PAT — если он есть;
// эндпоинты, которым PAT не нужен (как /admin/sources), его просто игнорируют.
const PAT_STORAGE_KEY = 'ovva_cw_pat';

export function getOvvaPat() {
  try {
    return localStorage.getItem(PAT_STORAGE_KEY) || '';
  } catch (e) {
    return '';
  }
}

export function setOvvaPat(value) {
  try {
    if (value) localStorage.setItem(PAT_STORAGE_KEY, value);
    else localStorage.removeItem(PAT_STORAGE_KEY);
  } catch (e) {
    // приватный режим браузера — токен просто не переживёт сессию
  }
}

export function useOvvaBackend() {
  const { accountId } = useAccount();

  async function requestCredentials() {
    const { data } = await axios.get(
      `/api/v1/accounts/${accountId.value}/ovva_chat_token`
    );
    return data;
  }

  async function callBackend(path, { method = 'GET', body } = {}) {
    let credentials = null;
    try {
      credentials = await requestCredentials();
    } catch (e) {
      const reason = e?.response?.data?.error;
      throw new Error(
        reason
          ? `Панель OVVA недоступна: ${reason}`
          : 'Панель OVVA недоступна — не удалось получить токен'
      );
    }

    const base = credentials?.backend_url;
    const token = credentials?.token;
    if (!base || !token) {
      throw new Error('Панель OVVA недоступна — бэкенд не выдал токен');
    }

    const pat = getOvvaPat();
    let res;
    try {
      res = await fetch(`${base}${path}`, {
        method,
        headers: {
          Authorization: `Bearer ${token}`,
          ...(pat ? { 'x-chatwoot-token': pat } : {}),
          ...(body ? { 'Content-Type': 'application/json' } : {}),
        },
        body: body ? JSON.stringify(body) : undefined,
      });
    } catch (e) {
      throw new Error(
        `Бэкенд OVVA не отвечает (${base}) — проверьте, что он запущен`
      );
    }

    // 401 — embed-токен панели (Bearer) не прошёл JwtAuthGuard, сообщение
    // сервера тут не про дело (сгенерирован автоматически, пользователь его
    // не вводил). 403 — иначе: RolesGuard/SuperAdminPatGuard/владение
    // ресурсом, сообщение сервера как раз то, что нужно показать (например,
    // «нужен личный Access Token супер-администратора» из SuperAdminPatGuard
    // на ручках каналов) — не глушим его тем же текстом про embed-токен.
    // .status на самой ошибке — чтобы вызывающий код отличал «доступ отклонён
    // (401/403, возврат к вводу PAT)» от обычной ошибки валидации/сети по
    // status, а не хрупким сравнением текста сообщения.
    if (res.status === 401) {
      const authError = new Error(
        'Бэкенд OVVA отклонил токен панели — проверьте OVVA_EMBED_TOKEN'
      );
      authError.status = 401;
      throw authError;
    }

    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      const error = new Error(data?.message || 'Не удалось выполнить действие');
      error.status = res.status;
      throw error;
    }
    return data;
  }

  return { callBackend };
}
